import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import StarField from "@/components/StarField";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2,
  Circle,
  KeyRound,
  LogOut,
  Search,
  ShieldCheck,
  Loader2,
  Clock,
  AlertTriangle,
  MessageSquarePlus,
  Trash2,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASS_KEY = "rts_admin_pass";
const SESSION_KEY = "rts_admin_session";
try { localStorage.removeItem(PASS_KEY); } catch {}

function getAdminSessionId() {
  try {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `sess_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return `sess_${Date.now()}`;
  }
}

function authHeaders(pass) {
  return { headers: { "X-Admin-Pass": pass } };
}

export default function AdminPage() {
  const [pass, setPass] = useState(() => {
    try {
      return sessionStorage.getItem(PASS_KEY) || "";
    } catch {
      return "";
    }
  });
  const [inputPass, setInputPass] = useState("");
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(false);
  const [items, setItems] = useState([]);
  const itemsRef = useRef([]);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  const [stats, setStats] = useState({ total: 0, prayed: 0, pending: 0, urgent_pending: 0 });
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all"); // all | pending | prayed
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = async (currentPass) => {
    const key = currentPass ?? pass;
    if (!key) return;
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (filter === "pending") params.prayed = false;
      if (filter === "prayed") params.prayed = true;
      const [listRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/prayer-requests`, { ...authHeaders(key), params }),
        axios.get(`${API}/admin/prayer-requests/stats`, authHeaders(key)),
      ]);
      setItems(listRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setVerified(false);
        setPass("");
        try { sessionStorage.removeItem(PASS_KEY); localStorage.removeItem(PASS_KEY); } catch {}
        toast.error("Session expired. Enter passphrase again.");
      } else {
        toast.error("Couldn't load prayer requests.");
      }
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    const candidate = inputPass.trim();
    if (!candidate) {
      toast.error("Enter the passphrase to continue.");
      return;
    }
    setChecking(true);
    try {
      await axios.post(`${API}/admin/verify`, { passphrase: candidate });
      setPass(candidate);
      try { sessionStorage.setItem(PASS_KEY, candidate); } catch {}
      setVerified(true);
      toast.success("You're in. Anonymously.");
      await load(candidate);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Incorrect passphrase.");
      } else if (err?.response?.status === 429) {
        toast.error("Too many tries. Please wait 15 minutes and try again.");
      } else {
        toast.error("Verification failed. Try again.");
      }
    } finally {
      setChecking(false);
    }
  };

  const signOut = () => {
    setPass("");
    setInputPass("");
    setVerified(false);
    setItems([]);
    try { sessionStorage.removeItem(PASS_KEY); localStorage.removeItem(PASS_KEY); } catch {}
  };

  const togglePrayed = async (id, next) => {
    try {
      const res = await axios.patch(
        `${API}/admin/prayer-requests/${id}/prayed?prayed=${next}`,
        {},
        authHeaders(pass),
      );
      setItems((list) => list.map((it) => (it.id === id ? res.data : it)));
      // Refetch fresh stats so urgent_pending stays accurate
      try {
        const s = await axios.get(
          `${API}/admin/prayer-requests/stats`,
          authHeaders(pass),
        );
        setStats(s.data);
      } catch {}
      toast.success(next ? "Marked as prayed." : "Reopened.");
    } catch {
      toast.error("Couldn't update this request.");
    }
  };

  // Auto-verify if we already have a stored pass
  useEffect(() => {
    if (pass && !verified) {
      (async () => {
        setChecking(true);
        try {
          await axios.post(`${API}/admin/verify`, { passphrase: pass });
          setVerified(true);
          await load(pass);
        } catch {
          setPass("");
          try { sessionStorage.removeItem(PASS_KEY); localStorage.removeItem(PASS_KEY); } catch {}
        } finally {
          setChecking(false);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (verified) {
      const t = setTimeout(() => load(), 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, filter, verified]);

  // Poll live "praying now" counts every 10s while signed in
  useEffect(() => {
    if (!verified || items.length === 0) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const ids = items.map((i) => i.id).join(",");
        const res = await axios.get(
          `${API}/admin/prayer-requests/praying-now?ids=${encodeURIComponent(ids)}`,
          authHeaders(pass),
        );
        if (cancelled) return;
        setItems((list) =>
          list.map((it) => ({ ...it, praying_now: res.data[it.id] ?? 0 })),
        );
      } catch {}
    };
    const iv = setInterval(tick, 10000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [verified, items.length, pass]);

  // Heartbeat while a request is expanded (I'm praying for this one)
  useEffect(() => {
    if (!verified || !selectedId) return;
    const sid = getAdminSessionId();
    const refreshCounts = async () => {
      try {
        const ids = itemsRef.current.map((i) => i.id).join(",");
        if (!ids) return;
        const res = await axios.get(
          `${API}/admin/prayer-requests/praying-now?ids=${encodeURIComponent(ids)}`,
          authHeaders(pass),
        );
        setItems((list) =>
          list.map((it) => ({ ...it, praying_now: res.data[it.id] ?? 0 })),
        );
      } catch {}
    };
    const beat = async () => {
      try {
        await axios.post(
          `${API}/admin/prayer-requests/${selectedId}/praying/heartbeat`,
          { session_id: sid },
          authHeaders(pass),
        );
        refreshCounts();
      } catch {}
    };
    beat();
    const iv = setInterval(beat, 12000);
    return () => {
      clearInterval(iv);
      // Best-effort stop signal (so count decrements quicker)
      axios
        .delete(
          `${API}/admin/prayer-requests/${selectedId}/praying/${sid}`,
          authHeaders(pass),
        )
        .then(refreshCounts)
        .catch(() => {});
    };
  }, [verified, selectedId, pass]);

  if (!verified) {
    return (
      <main
        data-testid="admin-login-page"
        className="relative min-h-screen pt-32 pb-16 px-6 md:px-10 overflow-hidden"
      >
        <StarField count={60} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]" />
        <div className="relative z-10 max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="rts-glass rounded-2xl p-8 md:p-10"
            style={{
              boxShadow:
                "0 0 60px rgba(234,179,8,0.15), inset 0 0 0 1px rgba(234,179,8,0.15)",
            }}
          >
            <ShieldCheck className="w-8 h-8 text-[#EAB308] mb-6" />
            <p className="font-mono-rts text-[0.68rem] tracking-[0.4em] uppercase text-[#EAB308]">
              Prayer Warrior
            </p>
            <h1 className="mt-3 font-serif-rts text-3xl md:text-4xl font-light">
              Anonymous sign on
            </h1>
            <p className="mt-3 text-sm text-white/60 leading-relaxed">
              No accounts. No names. Just the shared passphrase to open the
              inbox.
            </p>

            <form onSubmit={verify} className="mt-8 space-y-4" data-testid="admin-login-form">
              <label className="block text-[0.7rem] uppercase tracking-[0.28em] text-white/70">
                Passphrase
              </label>
              <div className="relative">
                <KeyRound className="w-4 h-4 text-white/40 absolute top-1/2 -translate-y-1/2 left-3" />
                <Input
                  type="password"
                  value={inputPass}
                  onChange={(e) => setInputPass(e.target.value)}
                  placeholder="Enter passphrase"
                  className="pl-10 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 h-11 rounded-md"
                  data-testid="admin-passphrase-input"
                  autoFocus
                />
              </div>
              <button
                type="submit"
                disabled={checking}
                className="rts-btn-primary w-full disabled:opacity-50"
                data-testid="admin-verify-btn"
              >
                {checking ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Verifying...
                  </>
                ) : (
                  "Open the Inbox"
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main
      data-testid="admin-page"
      className="relative min-h-screen pt-28 pb-16 px-4 md:px-8 overflow-hidden"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#05050A] via-[#070818] to-[#05050A]" />
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="font-mono-rts text-[0.68rem] tracking-[0.4em] uppercase text-[#EAB308]">
              Prayer Warrior Inbox
            </p>
            <h1 className="mt-2 font-serif-rts text-3xl md:text-4xl font-light">
              Requests waiting on the road.
            </h1>
          </div>
          <button
            onClick={signOut}
            className="rts-btn-ghost"
            data-testid="admin-signout-btn"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} testid="stat-total" />
          <StatCard label="Pending" value={stats.pending} accent testid="stat-pending" />
          <StatCard label="Prayed" value={stats.prayed} testid="stat-prayed" />
          <StatCard
            label="Urgent Pending"
            value={stats.urgent_pending}
            danger
            testid="stat-urgent"
          />
        </div>

        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-white/40 absolute top-1/2 -translate-y-1/2 left-3" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, request, email..."
              className="pl-10 bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 h-11"
              data-testid="admin-search-input"
            />
          </div>
          <div className="flex gap-2" data-testid="admin-filter-group">
            {["all", "pending", "prayed"].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-full text-[0.7rem] uppercase tracking-[0.22em] border transition-colors duration-300 ${
                  filter === f
                    ? "border-[#EAB308] text-[#EAB308] bg-[#EAB308]/10"
                    : "border-white/10 text-white/60 hover:text-white"
                }`}
                data-testid={`admin-filter-${f}`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="space-y-3" data-testid="admin-request-list">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-white/50">
              <Loader2 className="w-5 h-5 animate-spin mr-3" /> Loading requests...
            </div>
          ) : items.length === 0 ? (
            <div
              className="rts-glass rounded-xl p-10 text-center text-white/60"
              data-testid="admin-empty-state"
            >
              No prayer requests to show yet.
            </div>
          ) : (
            items.map((it) => (
              <RequestRow
                key={it.id}
                item={it}
                pass={pass}
                expanded={selectedId === it.id}
                onToggleExpand={() =>
                  setSelectedId((cur) => (cur === it.id ? null : it.id))
                }
                onTogglePrayed={togglePrayed}
                onUpdate={(updated) =>
                  setItems((list) =>
                    list.map((x) => (x.id === updated.id ? updated : x)),
                  )
                }
              />
            ))
          )}
        </div>
      </div>
    </main>
  );
}

function StatCard({ label, value, accent, danger, testid }) {
  const color = danger
    ? "text-[#F87171]"
    : accent
    ? "text-[#EAB308]"
    : "text-white";
  return (
    <div className="rts-glass rounded-xl p-5" data-testid={testid}>
      <p className="font-mono-rts text-[0.62rem] tracking-[0.3em] uppercase text-white/50">
        {label}
      </p>
      <p className={`mt-2 font-serif-rts text-3xl font-light ${color}`}>{value}</p>
    </div>
  );
}

function RequestRow({
  item: it,
  pass,
  expanded,
  onToggleExpand,
  onTogglePrayed,
  onUpdate,
}) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const notes = it.notes || [];

  const addNote = async (e) => {
    e?.preventDefault?.();
    const text = note.trim();
    if (!text) return;
    setSaving(true);
    try {
      const res = await axios.post(
        `${API}/admin/prayer-requests/${it.id}/notes`,
        { text },
        authHeaders(pass),
      );
      onUpdate(res.data);
      setNote("");
      toast.success("Note added.");
    } catch {
      toast.error("Couldn't add note.");
    } finally {
      setSaving(false);
    }
  };

  const removeNote = async (noteId) => {
    try {
      const res = await axios.delete(
        `${API}/admin/prayer-requests/${it.id}/notes/${noteId}`,
        authHeaders(pass),
      );
      onUpdate(res.data);
    } catch {
      toast.error("Couldn't remove note.");
    }
  };

  return (
    <div
      className={`rts-glass rounded-xl p-5 md:p-6 border transition-colors duration-300 ${
        it.prayed ? "border-white/5 opacity-90" : "border-white/10"
      } ${it.urgent && !it.prayed ? "!border-[#EAB308]/40" : ""}`}
      data-testid={`admin-request-${it.id}`}
    >
      <div className="flex items-start gap-4 flex-wrap md:flex-nowrap">
        <button
          onClick={() => onTogglePrayed(it.id, !it.prayed)}
          className={`mt-1 shrink-0 w-6 h-6 rounded-full flex items-center justify-center border transition-colors duration-300 ${
            it.prayed
              ? "border-[#EAB308] bg-[#EAB308]/20 text-[#EAB308]"
              : "border-white/30 text-transparent hover:border-[#EAB308] hover:text-[#EAB308]/40"
          }`}
          aria-label={it.prayed ? "Mark as unprayed" : "Mark as prayed"}
          data-testid={`admin-prayed-toggle-${it.id}`}
        >
          {it.prayed ? (
            <CheckCircle2 className="w-4 h-4" />
          ) : (
            <Circle className="w-4 h-4" />
          )}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span
              className="font-serif-rts text-lg text-white"
              data-testid={`admin-request-name-${it.id}`}
            >
              {it.name}
            </span>
            {it.urgent && (
              <span className="inline-flex items-center gap-1 text-[0.62rem] uppercase tracking-[0.25em] text-[#EAB308] border border-[#EAB308]/40 px-2 py-0.5 rounded-full">
                <AlertTriangle className="w-3 h-3" /> Urgent
              </span>
            )}
            {it.prayed && (
              <span className="text-[0.62rem] uppercase tracking-[0.25em] text-[#EAB308]">
                Prayed
              </span>
            )}
            {notes.length > 0 && (
              <span
                className="text-[0.62rem] uppercase tracking-[0.25em] text-white/60 border border-white/15 px-2 py-0.5 rounded-full"
                data-testid={`admin-notes-count-${it.id}`}
              >
                {notes.length} {notes.length === 1 ? "note" : "notes"}
              </span>
            )}
            {it.praying_now > 0 && (
              <span
                className="inline-flex items-center gap-1.5 text-[0.62rem] uppercase tracking-[0.25em] text-blue-300 border border-blue-300/40 bg-blue-300/5 px-2 py-0.5 rounded-full"
                data-testid={`admin-praying-now-${it.id}`}
                title="Warriors currently praying"
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-300 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-blue-300" />
                </span>
                <Users className="w-3 h-3" />
                {it.praying_now} praying
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-[0.68rem] font-mono-rts text-white/50 uppercase tracking-[0.18em] mb-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {formatDate(it.created_at)}
            </span>
            {it.email && <span>{it.email}</span>}
            {it.phone && <span>{it.phone}</span>}
          </div>
          <button
            onClick={onToggleExpand}
            className="text-left w-full"
            data-testid={`admin-request-body-${it.id}`}
          >
            <p
              className={`text-white/80 text-sm leading-relaxed ${
                expanded ? "" : "line-clamp-2"
              }`}
            >
              {it.request}
            </p>
          </button>

          {expanded && (
            <div className="mt-5 pt-5 border-t border-white/10" data-testid={`admin-notes-panel-${it.id}`}>
              <p className="font-mono-rts text-[0.62rem] tracking-[0.35em] uppercase text-[#EAB308] mb-4">
                Warrior Notes
              </p>

              {notes.length > 0 ? (
                <ul className="space-y-3 mb-5">
                  {notes.map((n) => (
                    <li
                      key={n.id}
                      className="flex items-start gap-3 bg-white/[0.03] border border-white/5 rounded-lg p-3"
                      data-testid={`admin-note-${n.id}`}
                    >
                      <div className="flex-1">
                        <p className="text-sm text-white/85 leading-relaxed">
                          {n.text}
                        </p>
                        <p className="mt-1 font-mono-rts text-[0.6rem] tracking-[0.25em] uppercase text-white/40">
                          {formatDate(n.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={() => removeNote(n.id)}
                        className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white/40 hover:text-[#F87171] hover:bg-white/5 transition-colors duration-300"
                        aria-label="Remove note"
                        data-testid={`admin-note-delete-${n.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-white/40 mb-4 italic">
                  No notes yet. Be the first warrior to leave an encouragement.
                </p>
              )}

              <form onSubmit={addNote} className="flex items-start gap-2" data-testid={`admin-note-form-${it.id}`}>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Leave a private note for the next warrior praying..."
                  rows={2}
                  className="bg-white/5 border-white/10 focus-visible:ring-[#EAB308] focus-visible:ring-1 rounded-md resize-none text-sm"
                  data-testid={`admin-note-input-${it.id}`}
                />
                <button
                  type="submit"
                  disabled={saving || !note.trim()}
                  className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#EAB308] text-[#EAB308] hover:bg-[#EAB308] hover:text-[#05050A] transition-colors duration-300 text-[0.7rem] uppercase tracking-[0.22em] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={`admin-note-submit-${it.id}`}
                >
                  <MessageSquarePlus className="w-3.5 h-3.5" />
                  {saving ? "..." : "Add"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDate(v) {
  if (!v) return "";
  try {
    const d = new Date(v);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}
